(() => {
  const slug = (value = '') => value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');




  /* ---- Movement cards: branded cover, then seven-card reveal --------------
     Begin with the merchant-selected card back so the setting is immediately
     visible. Reveal Pawn through Wild from left to right, pause on the full
     set, then turn the row back over and repeat. */
  const initFlipRow = (row) => {
    if (row.dataset.fcReady) return;
    row.dataset.fcReady = 'true';

    const cards = [...row.querySelectorAll('[data-fc-mcard]')];
    if (!cards.length) return;

    // The cover is the untransformed default face. This makes the uploaded
    // cover visible even before JavaScript starts or if motion is disabled.
    const setCover = (card, showCover) => {
      card.classList.toggle('is-revealed', !showCover);
      card.setAttribute('aria-pressed', String(!showCover));
    };

    const flipCard = (card) => setCover(card, card.classList.contains('is-revealed'));

    cards.forEach((card) => {
      setCover(card, true);
      card.addEventListener('click', () => flipCard(card));
      card.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        flipCard(card);
      });
    });

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      // Keep the useful seven-card overview static for visitors who opt out of
      // animation. Each card remains manually flippable by click or keyboard.
      cards.forEach((card) => setCover(card, false));
      return;
    }

    const timers = new Set();
    let running = false;

    const schedule = (callback, delay) => {
      const timer = window.setTimeout(() => {
        timers.delete(timer);
        callback();
      }, delay);
      timers.add(timer);
      return timer;
    };

    const stop = () => {
      timers.forEach((timer) => window.clearTimeout(timer));
      timers.clear();
      running = false;
    };

    const reveal = () => {
      cards.forEach((card, index) => {
        schedule(() => setCover(card, false), index * 260);
      });

      // Let shoppers study all seven unique faces before the next deal.
      schedule(showCovers, cards.length * 260 + 6000);
    };

    const showCovers = () => {
      cards.forEach((card, index) => {
        schedule(() => {
          if (!card.matches(':hover')) setCover(card, true);
        }, index * 180);
      });

      // Hold the uploaded cover long enough to read and recognise it.
      schedule(reveal, cards.length * 180 + 3000);
    };

    const start = () => {
      if (running) return;
      running = true;
      cards.forEach((card) => setCover(card, true));
      schedule(reveal, 3000);
    };

    // Only animate while the row is actually on screen.
    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) start();
          else if (!entry.isIntersecting) stop();
        });
      }, { threshold: 0.25 });
      observer.observe(row);
    } else {
      start();
    }

    // Hovering a covered card reveals its content for inspection.
    cards.forEach((card) => {
      card.addEventListener('mouseenter', () => setCover(card, false));
    });
  };

  const initGiftGrid = (grid) => {
    if (grid.dataset.fcReady) return;
    grid.dataset.fcReady = 'true';

    const tiles = [...grid.querySelectorAll('[data-fc-tile]')];
    if (!tiles.length) return;

    tiles.forEach((tile) => {
      tile.addEventListener('click', () => {
        const open = tile.classList.toggle('is-open');
        tile.setAttribute('aria-pressed', String(open));
      });
    });

    grid.querySelector('[data-fc-tile-reset]')?.addEventListener('click', () => {
      tiles.forEach((tile) => {
        tile.classList.remove('is-open');
        tile.setAttribute('aria-pressed', 'false');
      });
    });
  };

  const updateEditionPanels = (root) => {
    const productRoot = document.querySelector('product-information') || document.querySelector('featured-product-information');
    const variantInput = productRoot?.querySelector('form[action*="/cart/add"] input[name="id"]');
    const variantsNode = root.querySelector('[data-fc-variants]');
    if (!variantInput || !variantsNode) return;

    let variants = [];
    try { variants = JSON.parse(variantsNode.textContent); } catch (_) { return; }
    const variant = variants.find((item) => String(item.id) === String(variantInput.value));
    if (!variant) return;

    const edition = slug(variant.option1 || variant.title);
    const panels = [...root.querySelectorAll('[data-fc-edition]')];

    // Tolerant matching: the variant in Shopify may be named "Complete" while the
    // panel is set to "Complete Game" (or vice versa). Exact match wins; failing
    // that, accept either name containing the other.
    let match = panels.find((panel) => panel.dataset.fcEdition === edition);
    if (!match) {
      match = panels.find((panel) => {
        const name = panel.dataset.fcEdition || '';
        if (!name || !edition) return false;
        return name.startsWith(edition) || edition.startsWith(name);
      });
    }

    panels.forEach((panel) => panel.classList.toggle('is-current', panel === match));
  };

  const initPurchaseRoutes = (root) => {
    root.querySelectorAll('[data-fc-purchase-routes]').forEach((routes) => {
      if (routes.dataset.fcReady) return;
      routes.dataset.fcReady = 'true';

      const family = routes.querySelector('[data-fc-route="family"]');
      const gift = routes.querySelector('[data-fc-route="gift"]');
      const panel = routes.querySelector('[data-fc-gift-panel]');
      const giftInputs = [...routes.querySelectorAll('[data-fc-gift-input]')];

      const update = () => {
        const isGift = Boolean(gift?.checked);
        routes.querySelectorAll('.fc-route-card').forEach((card) => {
          card.classList.toggle('is-selected', Boolean(card.querySelector('input:checked')));
        });
        if (panel) panel.hidden = !isGift;
        if (gift) gift.setAttribute('aria-expanded', String(isGift));
        giftInputs.forEach((input) => {
          input.disabled = !isGift;
          if (input.hasAttribute('data-fc-gift-required')) input.required = isGift;
        });
      };

      family?.addEventListener('change', update);
      gift?.addEventListener('change', update);
      update();
    });
  };

  const initMotion = (root) => {
    // Any Finding Chess section participates in the reveal choreography.
    root.querySelectorAll('.fc-section').forEach((section) => {
      if (!section.hasAttribute('data-fc-reveal')) section.setAttribute('data-fc-reveal', '');
    });

    const items = [...root.querySelectorAll('[data-fc-reveal]')];
    if (!items.length) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches || !('IntersectionObserver' in window)) {
      items.forEach((item) => item.classList.add('is-visible'));
      return;
    }

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        observer.unobserve(entry.target);
      });
    }, { threshold: 0.16, rootMargin: '0px 0px -8% 0px' });

    items.forEach((item) => {
      if (item.dataset.fcMotionReady) return;
      item.dataset.fcMotionReady = 'true';
      observer.observe(item);
    });
  };

  const init = (scope = document) => {
    scope.querySelectorAll('[data-fc-gift-grid]').forEach(initGiftGrid);
    scope.querySelectorAll('[data-fc-flip-row]').forEach(initFlipRow);

    scope.querySelectorAll('[data-fc-edition-contents]').forEach((root) => {
      updateEditionPanels(root);
      document.addEventListener('change', () => requestAnimationFrame(() => updateEditionPanels(root)));
      document.addEventListener('click', () => requestAnimationFrame(() => updateEditionPanels(root)));
      const observer = new MutationObserver(() => updateEditionPanels(root));
      const productRoot = document.querySelector('product-information') || document.querySelector('featured-product-information');
      if (productRoot) observer.observe(productRoot, { attributes: true, childList: true, subtree: true });
    });

    initPurchaseRoutes(scope);
    initMotion(scope);
  };

  document.addEventListener('DOMContentLoaded', () => init());
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
